import React, { useState } from 'react';
import { toast } from 'sonner';
import { Icon } from '@/shared/icons';
import { downloadMediaWithFilename } from '../utils/downloadMedia';

interface DocumentContent {
  type: 'document';
  url: string;
  filename: string;
  size?: number;
  mimeType?: string;
}

interface DocumentPreviewProps {
  content: DocumentContent;
  isOwn?: boolean;
}

const extColorMap: Record<string, string> = {
  pdf: '#ef4444',
  doc: '#3b82f6',
  docx: '#3b82f6',
  xls: '#10b981',
  xlsx: '#10b981',
  ppt: '#f97316',
  pptx: '#f97316',
  txt: '#6b7280',
  csv: '#10b981',
  zip: '#a855f7',
  rar: '#a855f7',
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';

  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
};

const extractExt = (filename: string): string => {
  const match = filename.match(/\.([a-zA-Z0-9]+)$/);
  return match && match[1] ? match[1] : 'file';
};

/**
 * Component to display document messages
 *
 * @example
 * <DocumentPreview content={{
 *   type: 'document',
 *   url: 'https://...',
 *   filename: 'contract.pdf',
 *   size: 1024000
 * }} isOwn={true} />
 */
export const DocumentPreview: React.FC<DocumentPreviewProps> = ({ content, isOwn = false }) => {
  const filename = content.filename;
  const sizeLabel = formatFileSize(content.size);
  const ext = extractExt(filename);
  const extColor = extColorMap[ext.toLowerCase()] ?? '#ef4444';
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      await downloadMediaWithFilename(content.url, filename);
    } catch {
      toast.error('Falha ao baixar o arquivo');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2.5 py-1" data-testid="chat-message-document">
      <div
        className="w-10 h-12 flex-shrink-0 rounded-md text-[10px] font-extrabold flex items-end justify-center pb-1 text-white"
        style={{ background: extColor }}
      >
        {ext.toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold m-0 mb-0.5 truncate" title={filename}>
          {filename}
        </p>
        {sizeLabel && (
          <p className="text-[11px] opacity-70 m-0 tabular-nums">{sizeLabel}</p>
        )}
      </div>
      <button
        type="button"
        onClick={handleDownload}
        disabled={isDownloading}
        className="w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center border-0 cursor-pointer transition-opacity hover:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: isOwn ? 'rgba(255,255,255,0.2)' : 'var(--theme-primary, #6366f1)',
          color: '#fff',
        }}
        data-testid="chat-message-document-download"
        aria-label="Baixar"
      >
        {isDownloading ? (
          <Icon name="loader" className="w-4 h-4 animate-spin" />
        ) : (
          <Icon name="download" className="w-4 h-4" />
        )}
      </button>
    </div>
  );
};
